import React from "react";
import { statusCodes } from "../constants/EmailSender.constants";
import { app_pathnames, app_route_keys } from "constants/urlPaths";
import { domain_linking } from "features/Integration/constants/Integration.constants";
import { useHistory } from "react-router-dom";
import { CheckCircle, Error } from "@material-ui/icons";
import Tooltip from "ui/modules/Tooltip";
import { useDomainLinkingStore } from "../../DomainLinking/store/useDomainLinkingStore";
import { DOMAIN_LINKING_INTEGRATION_TABS } from "../../DomainLinking/constants/DomainLinking.constants";
import { useEmailSenderStore } from "../store/useEmailSenderStore";
import ExlyImage from "features/Common/modules/ExlyImage/ExlyImage";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import { useRefresh } from "react-admin";
import { EMAIL_SENDER_API_END_POINT } from "../constants/API";
import { getS3AssetCDNUrl } from "features/Common/modules/File/modules/Upload/utils/FileUpload.utils";
import { useCustomMutation } from "data/modules/api/hooks/useCustomMutation";

export const useEmailSenderUtils = () => {
  const history = useHistory();
  const refresh = useRefresh();
  const { notify } = useNotifications();
  const setActiveTab = useDomainLinkingStore((state) => state.setActiveTab);

  const redirectToDomainLinking = ({ tab }) => {
    if (tab) {
      setActiveTab(tab);
    }
    history.push(
      app_pathnames[app_route_keys.integration_details]({
        slug: domain_linking,
      })
    );
  };

  const handleSyncSuccess = () => {
    notify("Successfully synced mail record", notification_color_keys.success);
    refresh();
  };

  const handleSyncError = (error) => {
    notify(
      error?.body?.message ?? SOMETHING_WENT_WRONG,
      notification_color_keys.error
    );
  };

  const { mutate: syncEmail } = useCustomMutation({
    url: EMAIL_SENDER_API_END_POINT.sync,
    onSuccess: handleSyncSuccess,
    onError: handleSyncError,
  });

  const syncRecord = async ({ uuid }) => {
    if (!uuid) return;

    notify("Syncing mail record...");

    const payload = {
      uuid,
    };

    syncEmail(payload);
  };

  return {
    redirectToDomainLinking,
    syncRecord,
  };
};

export const StatusFormat = ({ record }) => {
  const status = record.status;

  const { redirectToDomainLinking, syncRecord } = useEmailSenderUtils();

  switch (status) {
    case statusCodes.connect_gmail:
      return (
        <p
          className="tw-text-[#493AB0] tw-underline tw-cursor-pointer"
          onClick={() =>
            redirectToDomainLinking(DOMAIN_LINKING_INTEGRATION_TABS.GUIDE)
          }
        >
          Connect your Gmail account
        </p>
      );
    case statusCodes.link_domain:
      return (
        <div
          className="tw-text-[#493AB0] tw-underline tw-cursor-pointer"
          onClick={() =>
            redirectToDomainLinking(DOMAIN_LINKING_INTEGRATION_TABS.GUIDE)
          }
        >
          Link your domain
        </div>
      );
    case statusCodes.domain_link_in_progress:
      return (
        <div className="tw-text-[#FFBC2B]">
          Domain linking in progress.
          <span
            className="tw-text-[#493AB0] tw-underline tw-cursor-pointer"
            onClick={() =>
              redirectToDomainLinking({
                tab: DOMAIN_LINKING_INTEGRATION_TABS.SETUP,
              })
            }
          >
            Check status
          </span>
        </div>
      );
    case statusCodes.sync_mail_records:
      return (
        <div
          className="tw-text-[#493AB0] tw-flex tw-gap-1 tw-cursor-pointer tw-items-center"
          onClick={() => syncRecord({ uuid: record.uuid })}
        >
          <span className="tw-underline">
            Sync mail records to complete setup
          </span>
          <ExlyImage
            src={getS3AssetCDNUrl("assets/images/email_sender/refresh.svg")}
          />
        </div>
      );
    case statusCodes.updating_mail_records:
      return <div className="tw-text-[#FFBC2B]">Updating mail records...</div>;
    case statusCodes.sender_id_approved:
      return (
        <div className="tw-inline-block tw-bg-[#00B779] tw-rounded-[2px] tw-px-[8px] tw-text-white">
          Sender ID approved
        </div>
      );
    case statusCodes.delink_existing_domain:
      return (
        <div
          className="tw-text-[#493AB0] tw-underline tw-cursor-pointer"
          onClick={() =>
            redirectToDomainLinking(
              DOMAIN_LINKING_INTEGRATION_TABS.DISCONNECTION
            )
          }
        >
          Delink existing domain and try again
        </div>
      );
    case statusCodes.link_domain_failed:
      return <div className="tw-text-[#BF2600]">Email linking sync failed</div>;
    default:
      return <div className="tw-text-[#493AB0]">N/A</div>;
  }
};

const StatusIcon = ({ record }) => {
  const status = record.status;

  if (status === statusCodes.sender_id_approved) {
    return <CheckCircle className="!tw-text-[#00B779]" />;
  }

  return <Error className="!tw-text-[#BF2600]" />;
};

export const EmailFormat = ({ record }) => {
  return (
    <div className="tw-flex tw-gap-2 tw-items-center">
      <StatusIcon record={record} />
      <span>{record.email}</span>
      {record.status === statusCodes.delink_existing_domain && (
        <Tooltip text="Your linked domain is different from the email domain. To use this email, delink your existing domain and try again." />
      )}
      {record.status === statusCodes.link_domain_failed &&
        record.email_sync_failed_reason && (
          <Tooltip text={record.email_sync_failed_reason} />
        )}
    </div>
  );
};

export const getEmailOptions = (isWhitelabelActive) => {
  const defaultConfigEmail = useEmailSenderStore.getState().defaultConfigEmail;
  const defaultSenderOption = {
    id: 0,
    label: "Send via Exly",
    value: defaultConfigEmail.email,
    name: defaultConfigEmail.name,
  };

  const emailSenderList = useEmailSenderStore.getState().emailSenderList;
  const userSenderOptions = emailSenderList.map((option, index) => {
    return {
      id: index + 1,
      label: option.email,
      value: option.email,
      name: option.name,
    };
  });

  let emailOptions;
  if (isWhitelabelActive) {
    emailOptions = [defaultSenderOption, ...userSenderOptions];
  } else {
    emailOptions = [defaultSenderOption];
  }
  return emailOptions;
};

export const getInitialOption = (email) => {
  const options = getEmailOptions(true);

  const selectedOption = options.find((option) => option.value === email);

  if (selectedOption) {
    return {
      name: selectedOption.name,
      email: selectedOption.value,
    };
  }

  const defaultOption = options.find((option) => option.id === 0);
  return {
    name: defaultOption.name,
    email: defaultOption.value,
  };
};
